import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV_LINKS } from "@/data/content";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "glass border-b border-slate-200/70 shadow-[0_1px_20px_rgba(15,23,42,0.04)]"
          : "bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-[72px]">
          <a href="#top" data-testid="brand-logo" className="flex items-center group">
            <span className="flex flex-col leading-none">
              <span className="text-[15px] font-semibold text-slate-900 font-heading tracking-tight">
                Verified Manufacturers™
              </span>
              <span className="text-[11px] text-slate-400 tracking-wide">
                a DeMaxtore workspace product
              </span>
            </span>
          </a>

          <div className="hidden lg:flex items-center gap-1">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-link-${l.href.replace("#", "")}`}
                className="px-3.5 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 rounded-full hover:bg-slate-100/70 transition-colors duration-200"
              >
                {l.label}
              </a>
            ))}
          </div>

          <div className="hidden lg:flex items-center gap-3">
            <a href="#final-cta" data-testid="nav-secondary-cta" className="text-sm font-medium text-slate-700 hover:text-slate-900 transition-colors duration-200">
              Sign in
            </a>
            <a href="#final-cta" data-testid="nav-primary-cta" className="bg-blue-600 text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-blue-700 transition-all duration-300 shadow-sm hover:shadow-md hover:-translate-y-0.5">
              Create Free Workspace
            </a>
          </div>

          <button
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-xl text-slate-700 hover:bg-slate-100 transition-colors"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden glass border-b border-slate-200/70 overflow-hidden"
          >
            <div className="px-4 sm:px-6 py-4 flex flex-col gap-1">
              {NAV_LINKS.map((l) => (
                <a key={l.href} href={l.href} onClick={() => setOpen(false)}
                  className="px-3 py-3 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-xl transition-colors">
                  {l.label}
                </a>
              ))}
              <a href="#final-cta" onClick={() => setOpen(false)}
                className="mt-2 bg-blue-600 text-white rounded-full px-5 py-3 text-sm font-medium text-center hover:bg-blue-700 transition-colors">
                Create Free Workspace
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
